<template>
  <div
    v-if="optionsAvailable"
    class="c-player-options"
  >
    <div
      v-if="audioTracksAvailable"
      class="c-player-options__column"
    >
      <h3>
        {{ $t('glossary.audio') }}:
      </h3>
      <ul>
        <li
          v-for="track in options.audioTracks"
          v-accessibility-focus="() => changeAudio(track)"
          :class="{ 'is-active': audioLang === track.lang }"
        >
          {{ $t(`language.${track.lang}`) }}
        </li>
      </ul>
    </div>
    <div
      v-if="subtitlesAvailable"
      class="c-player-options__column"
    >
      <h3>
        {{ $t('glossary.subtitles') }}:
      </h3>
      <ul>
        <li
          v-for="subtitle in options.subtitles"
          v-accessibility-focus="() => changeSubtitle(subtitle)"
          :class="{ 'is-active': settings.subtitle.language === subtitle.language }"
        >
          <span v-if="subtitle.language === SubtitlesLanguage.DISABLED">
            {{ $t('player.disableSubtitles') }}
          </span>
          <span v-else>
            {{ $t(`language.${subtitle.language}`) }}
          </span>
        </li>
      </ul>
    </div>
    <div
      v-if="profilesAvailable"
      class="c-player-options__column"
    >
      <h3>
        {{ $t('glossary.quality') }}:
      </h3>
      <ul>
        <li
          v-for="profile in options.profiles"
          v-accessibility-focus="() => changeProfile(profile)"
          class="c-player-options__select-item"
          :class="{ 'is-active': isProfileCurrent(profile) }"
        >
          <span>
            {{ formatProfile(profile) }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
  import { setAudio, setProfile, setSubtitle, state, toggleOptions } from '@/shared/components/player/store';
  import { SubtitlesLanguage } from '@/shared/components/player/enums';
  import { getTrackLanguage } from '@/shared/components/player/store-utils';
  import { NavigationContextManager } from '@/shared/utils/accessibility';

  export default {
    name: 'UiPlayerOptions',
    enums: {
      SubtitlesLanguage,
    },
    computed: {
      showOptions() {
        return state.showOptions;
      },
      options() {
        return state.options;
      },
      audioLang() {
        return getTrackLanguage(this.settings.audio);
      },
      audioTracksAvailable() {
        return this.options.audioTracks.length > 1;
      },
      subtitlesAvailable() {
        return this.options.subtitles.length > 1;
      },
      profilesAvailable() {
        return this.options.profiles.length;
      },
      optionsAvailable() {
        return this.audioTracksAvailable || this.subtitlesAvailable || this.profilesAvailable;
      },
      settings() {
        return state.settings;
      },
      currentProfile() {
        return this.settings.profile;
      },
    },
    mounted() {
      NavigationContextManager.pushContext(this.$el, toggleOptions, { savePrevFocus: true });
    },
    beforeDestroy() {
      NavigationContextManager.popContext();
    },
    methods: {
      changeAudio(track) {
        this.$storage.playerAudio = track;
        setAudio(track);
      },
      changeProfile(profile) {
        const { height, bandwidth, name } = profile;
        this.$storage.playerProfile = { height, bandwidth, name };
        setProfile(profile);
      },
      changeSubtitle(subtitle) {
        this.$storage.playerSubtitle = { language: subtitle.language };
        setSubtitle(subtitle);
      },
      formatProfile(profile) {
        const resolution = profile.width + ' x ' + profile.height;
        const bandwidth = `(${(profile.bandwidth / 10e5).toFixed(1)} Mb/s)`;

        if (profile.name) {
          return profile.name;
        }

        if (!profile.width || !profile.height) {
          return bandwidth;
        }

        return `${resolution} ${bandwidth}`;
      },
      isProfileCurrent(profile) {
        const property = profile.name ? 'name' : 'bandwidth';

        return profile[property] === this.currentProfile[property];
      },
    },
  };
</script>
